/**
 * Adding an image to a book (decision 37; part 16a log, table A), decided from what the store holds at one moment. Both
 * adapters read what {@link planImageAdd} needs inside the transaction that then writes its plan, so two adds (this tab
 * twice, two tabs, a tab without the lock) are one after the other (A10, A11).
 */
import { readImage, readImageBytes } from './records.ts';
import type { ImageBytesRecord, ImageRecord } from './records.ts';
import type { StoreFailure } from './store.types.ts';

/**
 * The outcome of `Store.addImage`: stored under its ID (A1); the book's image with the same hash, whose bytes the add
 * restored when they were damaged (A2, A3); the ID already stored in the book (A5); no room under the cap, with how much
 * the book's images use (A6); or the store failed (A9).
 */
export type AddImageResult =
  | { readonly kind: 'added' }
  | { readonly kind: 'existing'; readonly image: ImageRecord }
  | { readonly kind: 'taken' }
  | { readonly kind: 'full'; readonly used: number }
  | StoreFailure;

/** What an add reads of the book, raw, at one moment. */
export interface ImageAddReads {
  /** The book's image records with the image's hash (the book-and-hash index), in key order. */
  readonly sameHash: readonly unknown[];
  /** The image record stored under the new image's ID in the book, or undefined. */
  readonly record: unknown;
  /** The bytes record stored under `id` in the book, or undefined; asked for the new ID and for a same-hash image's. */
  bytes(id: string): unknown;
  /** Every image record of the book (the book index). */
  readonly book: readonly unknown[];
}

/** What an add decides: its outcome, and what it writes (nothing, or an image record with its bytes). */
export interface ImageAddPlan {
  readonly result: Exclude<AddImageResult, StoreFailure>;
  readonly write: { readonly image: ImageRecord; readonly bytes: ImageBytesRecord } | null;
}

/** The value `raw` read by `reader`, or undefined when the reader refuses it (A8). */
function readable<T>(reader: (raw: unknown) => T, raw: unknown): T | undefined {
  try {
    return reader(raw);
  } catch {
    return undefined;
  }
}

/** Whether `raw` is a readable bytes record holding exactly `bytes`. */
function holds(raw: unknown, bytes: Uint8Array): boolean {
  const stored = readable(readImageBytes, raw)?.bytes;
  return stored?.length === bytes.length && stored.every((byte, index) => byte === bytes[index]);
}

/** The first image of `records` the reader accepts (A8: a refused record is never a match). */
export function firstReadable(records: readonly unknown[]): ImageRecord | undefined {
  for (const raw of records) {
    const image = readable(readImage, raw);
    if (image !== undefined) return image;
  }
  return undefined;
}

/**
 * Table A: what adding `image` with `bytes` to its book does, under a cap of `cap` bytes for the book's images. The
 * caller has checked both records with their readers.
 */
export function planImageAdd(image: ImageRecord, bytes: Uint8Array, cap: number, reads: ImageAddReads): ImageAddPlan {
  const same = firstReadable(reads.sameHash);
  if (same !== undefined) {
    // A2: the bytes are there. A3: they aren't, or aren't these, which have the hash: they go back under that ID.
    if (holds(reads.bytes(same.id), bytes)) return { result: { kind: 'existing', image: same }, write: null };
    const restored = { ...image, id: same.id };
    return { result: { kind: 'existing', image: restored }, write: { image: restored, bytes: { bookId: image.bookId, id: same.id, bytes } } };
  }
  // A5, A8: an ID with anything under it, readable or not, is taken.
  if (reads.record !== undefined || reads.bytes(image.id) !== undefined) return { result: { kind: 'taken' }, write: null };
  let used = 0;
  for (const raw of reads.book) used += readable(readImage, raw)?.size ?? 0;
  // A6, A7: the cap may be reached, never passed.
  if (used + image.size > cap) return { result: { kind: 'full', used }, write: null };
  return { result: { kind: 'added' }, write: { image, bytes: { bookId: image.bookId, id: image.id, bytes } } };
}
